import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Box, Chip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ImageIcon from '@mui/icons-material/Image';
import styles from './CameraUpload.module.css';

interface CameraUploadProps {
  onFilesSelected: (files: File[]) => void;
  selectedFiles?: File[];
}

export const CameraUpload: React.FC<CameraUploadProps> = ({ onFilesSelected, selectedFiles = [] }) => {
  const { t } = useTranslation();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      onFilesSelected([...selectedFiles, ...files]);
      
      // Create preview URLs
      const previews = files.map(file => URL.createObjectURL(file));
      setPreview(prev => [...prev, ...previews]);
    }
    // Reset input so same file can be selected again
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    onFilesSelected(newFiles);
    
    // Revoke preview URL and remove from preview array
    if (preview[index]) {
      URL.revokeObjectURL(preview[index]);
    }
    setPreview(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className={styles.container}>
      <div className={styles.buttonGroup}>
        <Button
          type="button"
          variant="outlined"
          startIcon={<PhotoCameraIcon />}
          onClick={() => cameraInputRef.current?.click()}
          className={styles.button}
          sx={{
            backgroundColor: '#ffffff',
            borderColor: '#78B26E',
            color: '#333333',
            '&:hover': {
              backgroundColor: '#E1ECE2',
              borderColor: '#5E8856',
            },
            '& .MuiButton-startIcon': {
              marginRight: '8px',
              marginLeft: 0,
            },
            '[dir="rtl"] & .MuiButton-startIcon': {
              marginLeft: '8px',
              marginRight: 0,
            },
          }}
        >
          {t('General.Labels.TakePhoto')}
        </Button>
        <Button
          type="button"
          variant="outlined"
          startIcon={<ImageIcon />}
          onClick={() => galleryInputRef.current?.click()}
          className={styles.button}
          sx={{
            backgroundColor: '#ffffff',
            borderColor: '#78B26E',
            color: '#333333',
            '&:hover': {
              backgroundColor: '#E1ECE2',
              borderColor: '#5E8856',
            },
            '& .MuiButton-startIcon': {
              marginRight: '8px',
              marginLeft: 0,
            },
            '[dir="rtl"] & .MuiButton-startIcon': {
              marginLeft: '8px',
              marginRight: 0,
            },
          }}
        >
          {t('General.Labels.ChooseFromGallery')}
        </Button>
      </div>

      {/* Hidden camera input */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className={styles.hiddenInput}
        aria-label={t('General.Labels.TakePhoto')}
      />

      {/* Hidden gallery input - NO capture attribute to allow gallery selection */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        className={styles.hiddenInput}
        aria-label={t('General.Labels.ChooseFromGallery')}
      />

      {/* Preview images */}
      {(selectedFiles.length > 0 || preview.length > 0) && (
        <Box className={styles.previewContainer}>
          {selectedFiles.map((file, index) => {
            const previewUrl = preview[index] || URL.createObjectURL(file);
            return (
              <div key={index} className={styles.previewItem}>
                <img
                  src={previewUrl}
                  alt={`Preview ${index + 1}`}
                  className={styles.previewImage}
                />
                <Chip
                  label={file.name}
                  onDelete={() => handleRemoveFile(index)}
                  deleteIcon={<DeleteIcon />}
                  size="small"
                  className={styles.previewChip}
                />
              </div>
            );
          })}
        </Box>
      )}
    </div>
  );
};

export default CameraUpload;

